import { Input } from '@aqarmap/aqarmap-ui';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '~/hooks';
import { addListingDetailsType, ListingDetailsType } from '~addListing/types';
import {
  MONTHLY_DURATION,
  PAYMENT_METHOD_CASH_AND_INSTALLMENT_KEY,
  PAYMENT_METHOD_CASH_KEY,
  PAYMENT_METHOD_INSTALLMENT_KEY,
} from '~addListing/utils/data';

const PriceInput = ({ listing }: { listing: ListingDetailsType }) => {
  const { t } = useTranslation('app');
  const { mobileScreen } = useBreakpoint();
  const isForSale = listing?.section?.slug === 'for-sale';
  const area = listing?.area || 0;
  const defaultPrice = listing?.price || 0;

  const {
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<addListingDetailsType>();

  const paymentMethod = useWatch({ name: 'paymentMethod' });
  const paymentPlans = useWatch({ name: 'paymentPlans' }) || [];
  const paymentMethodCash = paymentMethod === PAYMENT_METHOD_CASH_KEY;
  const paymentMethodCashAndInstallment =
    paymentMethod === PAYMENT_METHOD_CASH_AND_INSTALLMENT_KEY;
  const paymentMethodInstallment =
    paymentMethod === PAYMENT_METHOD_INSTALLMENT_KEY;

  const pricePerMeter =
    watch('price') && area
      ? (watch('price') / area).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : 0;

  const handleChange = (value: string) => {
    setValue('price', Number(value.split(',').join('')) ?? '');

    if (!isForSale) {
      const newValue = {
        price: Number(value.split(',').join('')) ?? '',
        duration: MONTHLY_DURATION,
      };
      const updatedPaymentPlans = paymentPlans.map((item: any) =>
        item.duration === MONTHLY_DURATION ? newValue : item,
      );
      setValue('paymentPlans', updatedPaymentPlans);
    }
  };

  const hasErrors = errors.paymentPlans?.find?.(
    (item) => item?.price?.message === `${MONTHLY_DURATION}`,
  );

  const clearDailyErrors = () => {
    const errorIndex = errors.paymentPlans?.findIndex?.((item) => {
      return item?.price?.message === `${MONTHLY_DURATION}`;
    });

    if (errorIndex === -1) return;

    clearErrors(`paymentPlans.${Number(errorIndex)}.price`);
  };

  useEffect(() => {
    setValue('price', defaultPrice);
  }, [defaultPrice, paymentMethod]);

  return (
    <Input
      label={{
        component: (
          <p className="text-title-4 flex gap-x items-center">
            {paymentMethodInstallment ? t('cashPrice') : t('price')}
          </p>
        ),
      }}
      field={{
        type: 'text',
        placeholder: isForSale
          ? paymentMethodCashAndInstallment
            ? t('cashPricePlaceholder')
            : t('listingPrice')
          : t('rentMonthlyInputPlaceholder'),
        onChange: (e) => handleChange(e.target.value),
        onFocus: () => (isForSale ? clearErrors('price') : clearDailyErrors()),
        value: watch('price') ? watch('price')?.toLocaleString() : '',
      }}
      className="flex-1"
      endIcon={
        isForSale ? (
          <p className="border-s ps-x border-gray text-body_2 text-gray__dark_1">
            {mobileScreen ? t('pound') : t('egyptianPound')}
          </p>
        ) : (
          <div className="border-s ps-x border-gray text-body_2">
            <span className="text-gray__dark_2">
              {t('abbreviationEgyptianPound')} /{' '}
            </span>
            <span className="text-gray__dark_1">{t('month')}</span>
          </div>
        )
      }
      helper={{
        component:
          isForSale && paymentMethodCash && pricePerMeter ? (
            <div className="flex gap-0.5x">
              <p className="text-body_1 text-gray__dark_1">
                {t('pricePerMeter')}:
              </p>{' '}
              <p className="text-body_2 text-gray__dark_2">
                {pricePerMeter} {t('pricePerMeterUnit')}
              </p>
            </div>
          ) : (
            ''
          ),
      }}
      error={
        errors?.price || hasErrors
          ? errors?.price
            ? t('priceRequired')
            : t('monthlyPriceRequired')
          : ''
      }
    />
  );
};

export default PriceInput;
